import {
    createImageGallery,
    IImageGalleryItem,
    ImageGallery,
} from '@vfde-brix/ws10/image-gallery';
import { NO_PATTERN_BUSINESS_LOGIC } from '@vfde-brix/ws10/core';
import {
    SYSTEM_ICON_4G,
    SYSTEM_ICON_5G,
    SYSTEM_ICON_5G_PLUS,
} from '@vfde-brix/ws10/system-icon';
import { startAppListening } from 'Container/App/listener';
import {
    selectAtomicImages,
    selectCellular,
} from 'Container/App/selectors';
import { store } from '../store';
import { StateProps } from 'Container/App/interface';
import { areArraysEqual } from '@vfde-sails/utils';

const prepareImageGallery = (containerId: string) => {
    startAppListening({
        predicate: (action, currentState, previousState) =>
            !!selectAtomicImages(currentState).length && !selectAtomicImages(previousState).length,
        effect: (action, listenerApi) => {
            listenerApi.cancelActiveListeners();

            mountImageGallery(containerId);

            // We need this listener only for the initial mount.
            // After that, the listener in the mounting function will take over.
            listenerApi.unsubscribe();
        },
    });
};

/**
 * Mount ImageGallery component
 */
export const mountImageGallery = (
    containerId: string,
): ImageGallery | null => {
    const containerElement = document.getElementById(containerId);

    if (!containerElement) {
        /* istanbul ignore next */
        return null;
    }

    const images = selectAtomicImages(store.getState());
    const cellular = selectCellular(store.getState());

    const imageGallery = createImageGallery(containerElement, {
        items: convertImagesFromApiIntoImageGalleryItems(images),
        stdIconName: getImageGalleryIcon(cellular) || undefined,
        business: NO_PATTERN_BUSINESS_LOGIC,
    });

    startAppListening({
        predicate: (action, currentState, previousState) => {
            const currentAtomicImages = selectAtomicImages(currentState);
            const previousAtomicImages = selectAtomicImages(previousState);

            return !areArraysEqual(currentAtomicImages, previousAtomicImages);
        },
        effect: (action, listenerApi) => {
            listenerApi.cancelActiveListeners();

            const imageGalleryItems = convertImagesFromApiIntoImageGalleryItems(selectAtomicImages(listenerApi.getState()));
            imageGallery.updateItems(imageGalleryItems);
        },
    });

    return imageGallery;
};

const convertImagesFromApiIntoImageGalleryItems = (atomicImages: StateProps['atomicImages']): IImageGalleryItem[] => {
    const items: IImageGalleryItem[] = [];

    for (const image of atomicImages) {
        items.push({
            imgSrc: image,
            stdImgAlt: '',
        });
    }

    return items;
};

/**
 * Get Image Gallery Icon
 */
const getImageGalleryIcon = (cellular: string | null): string | null => {
    const cellularMap = {
        '4G': SYSTEM_ICON_4G,
        '5G': SYSTEM_ICON_5G,
        '5GPlus': SYSTEM_ICON_5G_PLUS,
    };

    return cellular && cellularMap[cellular as keyof typeof cellularMap];
};

export default prepareImageGallery;
